// ===== World 8: Penguins – FRICTION =====
// Ice is slippery (the ball slides a long way), gravel brakes a little, snow brakes a lot.
// Ball → goal: a penguin chick → a fish bucket.
// The chick has zero friction in the engine, so the "grip" of a surface is made with drag (speed x drag per frame while touching).
import { Registry, defineWorld, definePart, defineObj } from '../../core/registry';
import type { Ctx2D, Gfx, GameBody, IntroFn, Level, PartDef, Placement, PhysicsApi, Reskin, SkinDef, SoundFn, WorldMeta } from '../../core/types';
import { scene } from './scene';
import { levels } from './levels';
import { strings } from './strings';
import { K, DEG, SNOW, GRAVEL } from './shared';
const INK = '#3B2A5C';
const PW = 180, PH = 12, RL = 44;                 // size of the ice/snow/gravel parts

/** Fields this world keeps on the chick (and the helper balls / sliding penguin) */
interface PenguinBody extends GameBody {
  /** Fake ball in an intro animation (leans with its speed instead of rolling) */
  penguinFake?: boolean;
  _penguinX?: number;
  penguinRoll?: number;
  /** Surface the body is on now (kept for a few frames after a small hop) */
  penguinSurf?: Surf | null;
  /** Last frame the body touched a surface */
  penguinOff?: number;
  penguinFx?: number;
  penguinSnd?: number;
  penguinSplash?: boolean;
}
type Surf = 'ice' | 'snow' | 'gravel';
/** Draws a surface block (ice / snow / gravel) centred at x, y */
type BlockFn = (ctx: Ctx2D, x: number, y: number, w: number, h: number, a: number, sel: boolean, g: Gfx) => void;

// ---------- drawing helpers ----------
function rnd(i: number) { const s = Math.sin(i * 127.1 + 311.7) * 43758.5453; return s - Math.floor(s); }
function sparkle(ctx: Ctx2D, x: number, y: number, s: number, a?: number) {
  ctx.save(); ctx.translate(x, y); ctx.globalAlpha = a == null ? 1 : a; ctx.fillStyle = '#fff';
  ctx.beginPath(); ctx.moveTo(0, -s); ctx.quadraticCurveTo(0, 0, s, 0); ctx.quadraticCurveTo(0, 0, 0, s); ctx.quadraticCurveTo(0, 0, -s, 0); ctx.quadraticCurveTo(0, 0, 0, -s); ctx.fill();
  ctx.restore();
}
function iceBlock(ctx: Ctx2D, x: number, y: number, w: number, h: number, a: number, sel: boolean, g: Gfx) {
  g.block(ctx, x, y, w, h, a, '#E6FBFF', '#62BDEB', Math.min(8, h / 2), { outline: sel ? '#fff' : null });
  ctx.save(); ctx.translate(x, y); ctx.rotate(a);
  ctx.strokeStyle = 'rgba(255,255,255,.85)'; ctx.lineWidth = 3; ctx.lineCap = 'round';
  for (let px = -w / 2 + 22; px < w / 2 - 10; px += 46) { ctx.beginPath(); ctx.moveTo(px, h / 2 - 3); ctx.lineTo(px + 9, -h / 2 + 4); ctx.stroke(); }
  ctx.lineWidth = 1.6; ctx.strokeStyle = 'rgba(40,120,180,.35)';
  ctx.beginPath(); ctx.moveTo(-w / 2 + 12, h * 0.15); ctx.lineTo(w / 2 - 12, h * 0.15); ctx.stroke();
  for (let px = -w / 2 + 40; px < w / 2 - 20; px += 70) sparkle(ctx, px, -h / 2 - 2, 5);
  ctx.restore();
}
function snowBlock(ctx: Ctx2D, x: number, y: number, w: number, h: number, a: number, sel: boolean, g: Gfx) {
  g.block(ctx, x, y, w, h, a, '#FFFFFF', '#D5E6F5', Math.min(10, h / 2), { outline: sel ? '#fff' : null });
  ctx.save(); ctx.translate(x, y); ctx.rotate(a);
  // soft, round snow bumps on top
  const n = Math.max(3, Math.round((w - 10) / 22)), st = (w - 10) / n;
  ctx.beginPath();
  for (let i = 0; i < n; i++) { const cx = -w / 2 + 5 + st * (i + 0.5); ctx.moveTo(cx + st * 0.62, -h / 2 + 2); ctx.arc(cx, -h / 2 + 2, st * 0.62, 0, Math.PI, true); }
  ctx.fillStyle = '#FFFFFF'; ctx.fill(); ctx.strokeStyle = 'rgba(59,42,92,.85)'; ctx.lineWidth = 2.5; ctx.stroke();
  ctx.fillStyle = '#FFFFFF'; ctx.fillRect(-w / 2 + 6, -h / 2 + 1, w - 12, 5);
  ctx.fillStyle = 'rgba(110,170,230,.55)';
  for (let i = 0; i < w / 18; i++) { ctx.beginPath(); ctx.arc(-w / 2 + 10 + rnd(i + w) * (w - 20), -h / 2 + 6 + rnd(i * 3 + 1) * Math.max(2, h - 10), 1.8, 0, 7); ctx.fill(); }
  ctx.restore();
}
const PEB = ['#8C8799', '#B8957A', '#6E6A7E', '#D8CFC0', '#A48B6E'];
function gravelBlock(ctx: Ctx2D, x: number, y: number, w: number, h: number, a: number, sel: boolean, g: Gfx) {
  g.block(ctx, x, y, w, h, a, '#DCC5A6', '#9A8064', Math.min(8, h / 2), { outline: sel ? '#fff' : null });
  ctx.save(); ctx.translate(x, y); ctx.rotate(a);
  for (let i = 0; i < w / 7; i++) {
    const px = -w / 2 + 7 + rnd(i + 3) * (w - 14), py = -h / 2 + 1 + rnd(i * 7 + 2) * 7, r = 2.6 + rnd(i * 5) * 2.8;
    ctx.beginPath(); ctx.ellipse(px, py, r * 1.2, r, rnd(i) * 3, 0, 7); ctx.fillStyle = PEB[i % PEB.length]; ctx.fill();
    ctx.lineWidth = 1.2; ctx.strokeStyle = 'rgba(59,42,92,.55)'; ctx.stroke();
  }
  ctx.restore();
}
function rockBlock(ctx: Ctx2D, x: number, y: number, w: number, h: number, a: number, g: Gfx) {
  g.block(ctx, x, y, w, h, a, '#B3BBCD', '#6C7690', Math.min(14, w / 2, h / 2));
  ctx.save(); ctx.translate(x, y); ctx.rotate(a);
  ctx.strokeStyle = 'rgba(59,42,92,.35)'; ctx.lineWidth = 2.5; ctx.lineCap = 'round';
  for (let i = 0; i < Math.max(1, w * h / 9000); i++) {
    const px = (rnd(i + w) - .5) * (w - 24), py = (rnd(i + h) - .5) * (h - 24);
    ctx.beginPath(); ctx.moveTo(px - 8, py - 6); ctx.lineTo(px, py); ctx.lineTo(px + 4, py + 10); ctx.stroke();
  }
  ctx.restore();
}
// small grown-up penguin (standing), h = height
function penguinStand(ctx: Ctx2D, x: number, y: number, h: number, t: number, flap: boolean, flip: boolean) {
  const k = h / 100;
  ctx.save(); ctx.translate(x, y); ctx.scale(k * (flip ? -1 : 1), k);
  ctx.rotate(Math.sin(t * 0.06) * 0.05);
  ctx.fillStyle = '#FF9F2E'; [-14, 14].forEach(s => { ctx.beginPath(); ctx.ellipse(s, -3, 13, 5, 0, 0, 7); ctx.fill(); });
  ctx.beginPath(); ctx.ellipse(0, -48, 30, 46, 0, 0, 7); ctx.fillStyle = '#2E3350'; ctx.fill(); ctx.lineWidth = 3 / k; ctx.strokeStyle = INK; ctx.stroke();
  ctx.beginPath(); ctx.ellipse(3, -40, 21, 36, 0, 0, 7); ctx.fillStyle = '#FFFFFF'; ctx.fill();
  ctx.beginPath(); ctx.ellipse(6, -72, 15, 7, 0, 0, 7); ctx.fillStyle = '#FFD24A'; ctx.fill();
  const fa = flap ? Math.sin(t * 0.5) * 0.7 : 0;
  ctx.fillStyle = '#2E3350';
  [[-1, -28], [1, 28]].forEach(([s, px]) => { ctx.save(); ctx.translate(px, -58); ctx.rotate(s * (0.25 + fa)); ctx.beginPath(); ctx.ellipse(s * 4, 18, 7, 22, 0, 0, 7); ctx.fill(); ctx.restore(); });
  ctx.fillStyle = '#fff'; ctx.beginPath(); ctx.arc(10, -80, 7, 0, 7); ctx.fill();
  ctx.fillStyle = INK; ctx.beginPath(); ctx.arc(12, -80, 3.6, 0, 7); ctx.fill();
  ctx.fillStyle = '#FF9F2E'; ctx.beginPath(); ctx.moveTo(22, -78); ctx.lineTo(38, -73); ctx.lineTo(22, -69); ctx.closePath(); ctx.fill();
  ctx.fillStyle = 'rgba(255,120,150,.5)'; ctx.beginPath(); ctx.ellipse(12, -66, 5, 3, 0, 0, 7); ctx.fill();
  ctx.restore();
}
// penguin sliding on its belly (centred), dir = 1 (head to the right) or -1
function penguinBelly(ctx: Ctx2D, x: number, y: number, a: number, dir: number, sel: boolean, t: number, g: Gfx) {
  ctx.save(); ctx.translate(x, y); ctx.rotate(a); ctx.scale(dir, 1);
  g.shadowOn(ctx, 8, 4);
  ctx.beginPath(); ctx.ellipse(0, 0, 34, 16, 0, 0, 7); ctx.fillStyle = '#2E3350'; ctx.fill(); g.shadowOff(ctx);
  ctx.lineWidth = 3; ctx.strokeStyle = INK; ctx.stroke();
  ctx.beginPath(); ctx.ellipse(-2, 7, 28, 8, 0, 0, 7); ctx.fillStyle = '#fff'; ctx.fill();         // belly (facing down)
  ctx.fillStyle = '#FF9F2E';                                                                          // feet pointing back
  ctx.beginPath(); ctx.ellipse(-36, 4, 9, 4, 0.3, 0, 7); ctx.fill(); ctx.beginPath(); ctx.ellipse(-35, -3, 8, 4, -0.2, 0, 7); ctx.fill();
  ctx.fillStyle = '#FFD24A'; ctx.beginPath(); ctx.ellipse(20, 2, 7, 5, 0, 0, 7); ctx.fill();
  ctx.fillStyle = '#fff'; ctx.beginPath(); ctx.arc(24, -5, 5.5, 0, 7); ctx.fill();
  ctx.fillStyle = INK; ctx.beginPath(); ctx.arc(26, -5, 2.8, 0, 7); ctx.fill();
  ctx.fillStyle = '#FF9F2E'; ctx.beginPath(); ctx.moveTo(31, -2); ctx.lineTo(44, 1); ctx.lineTo(31, 5); ctx.closePath(); ctx.fill();
  ctx.fillStyle = '#2E3350'; ctx.save(); ctx.translate(-4, -4); ctx.rotate(-0.5 + Math.sin((t || 0) * 0.3) * 0.15); ctx.beginPath(); ctx.ellipse(-12, 0, 16, 5, 0, 0, 7); ctx.fill(); ctx.restore();
  if (sel) { ctx.beginPath(); ctx.ellipse(2, 0, 46, 24, 0, 0, 7); ctx.strokeStyle = '#FFC93C'; ctx.lineWidth = 4; ctx.stroke(); }
  ctx.restore();
}
function fish(ctx: Ctx2D, x: number, y: number, s: number, a: number, col: string) {
  ctx.save(); ctx.translate(x, y); ctx.rotate(a); ctx.scale(s, s);
  ctx.fillStyle = col; ctx.beginPath(); ctx.ellipse(0, 0, 16, 8, 0, 0, 7); ctx.fill();
  ctx.beginPath(); ctx.moveTo(13, 0); ctx.lineTo(25, -8); ctx.lineTo(25, 8); ctx.closePath(); ctx.fill();
  ctx.lineWidth = 2; ctx.strokeStyle = INK; ctx.beginPath(); ctx.ellipse(0, 0, 16, 8, 0, 0, 7); ctx.stroke();
  ctx.fillStyle = INK; ctx.beginPath(); ctx.arc(-9, -2, 1.8, 0, 7); ctx.fill();
  ctx.restore();
}

// intro animations
function introSurface(drawFn: BlockFn, stopAt: number): IntroFn {
  return (ctx, t, g, h) => {
    // sloping ice ramp on the left, then the surface
    iceBlock(ctx, 90, 120, 150, 16, 30 * DEG, false, g);
    drawFn(ctx, 300, 196, 260, 18, 0, false, g);
    const k = t / 200;
    let x, y;
    const x0 = 40, y0 = 70, x1 = 165, y1 = 172;
    if (k < 0.25) { const e = (k / 0.25) ** 2; x = x0 + (x1 - x0) * e; y = y0 + (y1 - y0) * e; }
    else { const s = Math.min(1, (k - 0.25) / 0.6); const d = stopAt * (1 - (1 - s) ** (stopAt > 300 ? 1 : 2.5)); x = x1 + d; y = 172; }
    if (x > 440) { x = 440; }
    const b: PenguinBody = h.fakeBall(x, y, 3, 0, 18); b.penguinFake = true;
    drawChick(ctx, b, 'hero', t, g);
  };
}

// ---------- skins ----------
const skins: Record<string, SkinDef> = {
  penguins_ice(ctx, b, g) { iceBlock(ctx, b.position.x, b.position.y, b.dw!, b.dh!, b.angle, false, g); },
  penguins_snow(ctx, b, g) { snowBlock(ctx, b.position.x, b.position.y, b.dw!, b.dh!, b.angle, false, g); },
  penguins_gravel(ctx, b, g) { gravelBlock(ctx, b.position.x, b.position.y, b.dw!, b.dh!, b.angle, false, g); },
  penguins_rock(ctx, b, g) { rockBlock(ctx, b.position.x, b.position.y, b.dw!, b.dh!, b.angle, g); },
};

// ---------- parts ----------
// A simple part (ice / snow / gravel): static rectangle + drag
function surfPart(kind: string, drawFn: BlockFn, extra: PartDef): PartDef {
  const base: PartDef = {
    rects: [{ x: 0, y: 0, w: PW, h: PH }], rot: true, snap: true, defaultA: 15,
    draw(ctx, pl, sel, g) { const a = (pl.a || 0) * DEG; drawFn(ctx, pl.x - Math.sin(a) * 2, pl.y + Math.cos(a) * 2, PW, PH + 4, a, sel, g); },
    icon(ctx, g) { drawFn(ctx, 0, 4, 100, 18, -0.35, false, g); },
    // physics: a middle block + two small sloping ramps at the ends, so the ball can ride up onto the part when it lies on top of ice
    create(api, pl, info) {
      const a = (pl.a || 0) * DEG, c = Math.cos(a), s = Math.sin(a), pd = Registry.parts[kind];
      const W = (lx: number, ly: number) => ({ x: pl.x + lx * c - ly * s, y: pl.y + lx * s + ly * c });
      const opts = { isStatic: true, friction: 0, restitution: pd.restitution || 0, label: kind };
      const mk = (lx: number, ly: number, w: number, h: number, da: number) => { const p = W(lx, ly); const b = api.add(api.R(p.x, p.y, w, h, Object.assign({ angle: a + da }, opts))); b.kind = kind; b.partIdx = info.idx; b.fixedPart = info.fixed; if (pd.drag) { b.drag = pd.drag; api.S.drags.add(b); } return b; };
      mk(0, 0, PW - 2 * RL + 4, PH, 0);
      const ra = Math.atan2(PH, RL), rl = Math.hypot(PH, RL), th = 6;
      for (const sg of [-1, 1]) {
        const mx = sg * (PW / 2 - RL / 2), nx = Math.sin(ra) * th / 2, ny = Math.cos(ra) * th / 2;
        mk(mx - sg * nx, ny, rl, th, sg * ra);
      }
      return { layer: 'back', draw(ctx, t, g) { drawFn(ctx, pl.x - s * 2, pl.y + c * 2, PW, PH + 4, a, false, g); } };
    },
  };
  return Object.assign(base, extra);
}

const parts = {
  penguins_ice: surfPart('penguins_ice', iceBlock, { friction: 0, restitution: 0.1,
    intro: introSurface(iceBlock, 400) }),
  penguins_snow: surfPart('penguins_snow', snowBlock, { friction: 0, restitution: 0, drag: SNOW,
    intro: introSurface(snowBlock, 60) }),
  penguins_gravel: surfPart('penguins_gravel', gravelBlock, { friction: 0, restitution: 0.05, drag: GRAVEL,
    intro: introSurface(gravelBlock, 170) }),
  // Penguin sliding on its belly and pushing the ball. It slides far on ice and stops in snow.
  penguins_penguin: definePart({
    rects: [{ x: 0, y: 0, w: 66, h: 30 }], rot: 15, defaultA: 15,
    blockers(pl) { return [{ x: pl.x, y: pl.y, r: 40 }]; },
    draw(ctx, pl, sel, g) { const a = pl.a || 0; penguinBelly(ctx, pl.x, pl.y, a * DEG, a < 0 ? -1 : 1, sel, 0, g); },
    icon(ctx, g) { penguinBelly(ctx, -4, 0, 0.3, 1, false, 0, g); },
    create(api, pl) {
      const a = (pl.a || 0) * DEG;
      const b = api.add(api.R(pl.x, pl.y, 64, 28, { angle: a, density: 0.004, friction: 0, frictionStatic: 0, frictionAir: 0.002, restitution: 0.1, chamfer: { radius: 13 }, label: 'penguins_penguin' }));
      b.kind = 'penguins_penguin'; api.dynamic(b);
      let dir = (pl.a || 0) < 0 ? -1 : 1;
      api.onHit(b, o => { if (api.isBall(o)) api.sfx('penguins_squeak', b.position.x, b.position.y - 20, '#FFD24A', 6); });
      return {
        layer: 'mid',
        draw(ctx, t, g) { if (Math.abs(b.velocity.x) > 0.6) dir = b.velocity.x > 0 ? 1 : -1; penguinBelly(ctx, b.position.x, b.position.y, b.angle, dir, false, b.speed > 0.5 ? t : 0, g); },
      };
    },
    intro(ctx, t, g, h) {
      iceBlock(ctx, 150, 130, 240, 16, 25 * DEG, false, g);
      iceBlock(ctx, 360, 196, 200, 16, 0, false, g);
      const k = t / 240;
      const pk = Math.min(1, k / 0.4), px = 60 + 200 * pk * pk, py = 86 + 97 * pk * pk;
      penguinBelly(ctx, Math.min(px, 250), Math.min(py, 172), px < 250 ? 25 * DEG : 0, 1, false, t, g);
      const bx = k < 0.4 ? 280 : 280 + Math.min(150, (k - 0.4) * 500);
      const b: PenguinBody = h.fakeBall(bx, 170, k > 0.4 ? 4 : 0, 0, 18); b.penguinFake = true;
      drawChick(ctx, b, 'hero', t, g);
    },
  }),
};

// ---------- objects ----------
const objs = {
  // decoration: a grown-up penguin standing and watching (flaps its flippers when you win)
  penguins_friend: defineObj({
    layer: 'back',
    create() { return {}; },
    draw(ctx, st, t, g, S) { const o = st.o; ctx.beginPath(); ctx.ellipse(o.x, o.y + 4, 46, 11, 0, 0, 7); ctx.fillStyle = '#EAF8FF'; ctx.fill(); ctx.lineWidth = 3; ctx.strokeStyle = INK; ctx.stroke(); penguinStand(ctx, o.x, o.y, (o.h as number) || 70, t + o.x, S && S.won, o.flip as boolean); },
  }),
};

// ---------- sounds ----------
const sounds: Record<string, SoundFn> = {
  penguins_slide: (tone) => { tone(1900, 0.14, 'sine', 0.05, 2800); tone(2600, 0.12, 'triangle', 0.03, 3400, 0.06); },
  penguins_crunch: (tone, noise) => { noise(0.14, 0.22, 900, 0.8); noise(0.08, 0.12, 1800, 1, 0.07); },
  penguins_gravel: (tone, noise) => { noise(0.06, 0.18, 2600, 1.5); noise(0.05, 0.14, 2000, 1.5, 0.05); },
  penguins_splash: (tone, noise) => { noise(0.45, 0.4, 600, 0.6); tone(320, 0.3, 'sine', 0.2, 90); },
  penguins_squeak: (tone) => { tone(900, 0.09, 'square', 0.07, 1400); tone(1200, 0.1, 'square', 0.06, 800, 0.1); },
};

// ---------- surfaces and the sea ----------
// Which surface does a body touch?
const SURF: Partial<Record<string, Surf>> = { penguins_ice: 'ice', penguins_snow: 'snow', penguins_gravel: 'gravel', plank: 'ice', long_plank: 'ice' };
function surfOf(b: GameBody) { return b.kind === 'static' ? SURF[b.skin as string] : SURF[b.kind as string]; }
/** Particles per surface: colour, count, every n frames */
const FX: Record<Surf, [string, number, number]> = { ice: ['#DFFBFF', 2, 6], snow: ['#FFFFFF', 3, 5], gravel: ['#B8957A', 2, 6] };

// For every level: sparkles on ice, puffs on snow, gravel crunch – and the sea at the bottom (splash!)
function setup(api: PhysicsApi, level: Level) {
  const sea = level.sea === false ? null : ((level.sea as number | undefined) || 740);
  const S = api.S;
  api.thing({
    layer: 'front',
    step(api: PhysicsApi) {
      for (const b of S.dyn as PenguinBody[]) {
        if (!(b.kind === 'hero' || b.kind === 'helper' || b.kind === 'penguins_penguin')) continue;
        let surf: Surf | null = null;
        for (const o of api.touching(b)) { const s = surfOf(o); if (s) { surf = s; if (s !== 'ice') break; } }
        if (!surf && b.penguinSurf && b.penguinOff != null && S.t - b.penguinOff < 8) surf = b.penguinSurf;     // short hop: keep the surface
        if (surf) b.penguinOff = S.t;
        const prev = b.penguinSurf; b.penguinSurf = surf || (b.penguinSurf && S.t - (b.penguinOff || 0) < 8 ? b.penguinSurf : null);
        const sp = b.speed, bx = b.position.x, by = b.position.y + (b.circleRadius || 14) - 2;
        if (!surf) continue;
        const f = FX[surf];
        if (surf !== prev) { b.penguinFx = S.t; if (sp > 0.8) api.sfx(surf === 'ice' ? 'penguins_slide' : surf === 'snow' ? 'penguins_crunch' : 'penguins_gravel', bx, by, f[0], surf === 'snow' ? 10 : 6); b.penguinSnd = S.t; continue; }
        // '' = particles only, no sound
        if (sp > 0.8 && S.t % f[2] === 0) api.sfx('', bx, by, f[0], f[1]);
        if (sp > 1.5 && S.t - (b.penguinSnd || 0) > (surf === 'ice' ? 24 : 16)) { b.penguinSnd = S.t; api.sfx(surf === 'ice' ? 'penguins_slide' : surf === 'snow' ? 'penguins_crunch' : 'penguins_gravel', bx, by); }
      }
      if (sea) for (const h of S.heroes as PenguinBody[]) if (!h.penguinSplash && h.position.y > sea) { h.penguinSplash = true; api.sfx('penguins_splash', h.position.x, sea, '#9FD8FF', 16); }
    },
    draw(ctx: Ctx2D, t: number) {
      if (!sea) return;
      ctx.fillStyle = 'rgba(70,160,225,.82)'; ctx.fillRect(0, sea, 1200, 800 - sea);
      ctx.strokeStyle = 'rgba(255,255,255,.8)'; ctx.lineWidth = 4; ctx.lineCap = 'round';
      for (let row = 0; row < 2; row++) {
        ctx.beginPath();
        const yy = sea + 2 + row * 24, off = (t * (row ? -0.6 : 0.8)) % 80;
        for (let x = -80 + off; x < 1260; x += 80) { ctx.moveTo(x, yy); ctx.quadraticCurveTo(x + 20, yy - 8, x + 40, yy); ctx.quadraticCurveTo(x + 60, yy + 8, x + 80, yy); }
        ctx.stroke();
      }
    },
  });
}

// ---------- reskins: ball, goal and kit parts in the penguin style ----------
// The hero: a round, fluffy penguin chick. On ice it slides (stays upright), otherwise it rolls.
function drawChick(ctx: Ctx2D, body: GameBody, kind: string, t: number, g: Gfx) {
  const b = body as PenguinBody;
  const { x, y } = b.position, r = b.circleRadius!, v = b.velocity || { x: 0, y: 0 };
  let rot;
  if (b.id == null) rot = b.penguinFake ? Math.max(-.3, Math.min(.3, v.x * .05)) : (b.roll || 0);
  else {
    if (b._penguinX == null) b._penguinX = x;
    const dx = x - b._penguinX; b._penguinX = x;
    b.penguinRoll = b.penguinRoll || 0;
    if (b.penguinSurf === 'ice') {
      b.penguinRoll = Math.atan2(Math.sin(b.penguinRoll), Math.cos(b.penguinRoll));
      const tgt = Math.max(-.35, Math.min(.35, v.x * 0.05));
      b.penguinRoll += (tgt - b.penguinRoll) * 0.2;
    } else b.penguinRoll += dx / r;
    rot = b.penguinRoll;
  }
  ctx.save(); ctx.translate(x, y);
  g.shadowOn(ctx, 8, 5);
  const gr = ctx.createRadialGradient(-r * .35, -r * .4, r * .15, 0, 0, r * 1.1);
  if (kind === 'hero') { gr.addColorStop(0, '#F4F7FB'); gr.addColorStop(.55, '#BCC6D4'); gr.addColorStop(1, '#8491A6'); }
  else { gr.addColorStop(0, '#FFFFFF'); gr.addColorStop(1, '#C9DCEE'); }
  ctx.beginPath(); ctx.arc(0, 0, r, 0, 7); ctx.fillStyle = gr; ctx.fill(); g.shadowOff(ctx);
  ctx.rotate(rot);
  if (kind === 'hero') {
    // dark cap + white face mask
    ctx.save(); ctx.beginPath(); ctx.arc(0, 0, r, 0, 7); ctx.clip();
    ctx.fillStyle = '#343A55'; ctx.beginPath(); ctx.ellipse(0, -r * .55, r * 1.1, r * .75, 0, 0, 7); ctx.fill();
    ctx.fillStyle = '#FFFFFF'; ctx.beginPath(); ctx.ellipse(0, -r * .02, r * .72, r * .5, 0, 0, 7); ctx.fill();
    ctx.restore();
    // tuft of down
    ctx.strokeStyle = '#343A55'; ctx.lineWidth = 2; ctx.lineCap = 'round';
    ctx.beginPath(); ctx.moveTo(0, -r); ctx.quadraticCurveTo(-2, -r - 6, 3, -r - 7); ctx.moveTo(1, -r); ctx.quadraticCurveTo(5, -r - 4, 7, -r - 3); ctx.stroke();
  }
  ctx.lineWidth = 2.5; ctx.strokeStyle = INK; ctx.beginPath(); ctx.arc(0, 0, r, 0, 7); ctx.stroke();
  // eyes
  const sp = Math.hypot(v.x, v.y), scared = v.y > 9, fast = sp > 8;
  const lx = Math.max(-2, Math.min(2, v.x * 0.4)), ly = Math.max(-2, Math.min(2, v.y * 0.4));
  const ex = r * .32, ey = -r * .12, er = r * .2;
  for (const s of [-1, 1]) {
    ctx.beginPath(); ctx.arc(s * ex, ey, er * 1.2, 0, 7); ctx.fillStyle = '#fff'; ctx.fill();
    ctx.beginPath(); ctx.arc(s * ex + lx * .5, ey + ly * .5, er * (scared ? .55 : .85), 0, 7); ctx.fillStyle = INK; ctx.fill();
    ctx.beginPath(); ctx.arc(s * ex + lx * .5 + 1.2, ey + ly * .5 - 1.4, er * .3, 0, 7); ctx.fillStyle = '#fff'; ctx.fill();
  }
  // beak
  ctx.fillStyle = '#FF9F2E'; ctx.strokeStyle = INK; ctx.lineWidth = 1.3;
  ctx.beginPath();
  if (b.sad) { ctx.moveTo(-r * .18, r * .3); ctx.lineTo(0, r * .18); ctx.lineTo(r * .18, r * .3); ctx.closePath(); }
  else if (fast || scared) { ctx.moveTo(-r * .2, r * .12); ctx.lineTo(r * .2, r * .12); ctx.lineTo(0, r * .5); ctx.closePath(); }
  else { ctx.moveTo(-r * .2, r * .14); ctx.lineTo(r * .2, r * .14); ctx.lineTo(0, r * .36); ctx.closePath(); }
  ctx.fill(); ctx.stroke();
  ctx.fillStyle = 'rgba(255,120,150,.5)';
  [-1, 1].forEach(s => { ctx.beginPath(); ctx.ellipse(s * r * .6, r * .2, r * .14, r * .09, 0, 0, 7); ctx.fill(); });
  ctx.restore();
  // shine
  ctx.beginPath(); ctx.ellipse(x - r * .4, y - r * .5, r * .22, r * .13, -0.6, 0, 7); ctx.fillStyle = 'rgba(255,255,255,.55)'; ctx.fill();
}

// The goal: a fish bucket
type GoalArea = { x: number; y: number; w: number };
const goal = {
  bowl(ctx: Ctx2D, gl: GoalArea, full: number, t: number, g: Gfx) {
    const { x, y, w } = gl;
    ctx.save();
    // handle behind
    ctx.strokeStyle = '#5D6B86'; ctx.lineWidth = 5; ctx.beginPath(); ctx.ellipse(x, y - 58, w / 2 - 6, 46, 0, Math.PI, 0); ctx.stroke();
    // fish tails sticking out
    if (!full) { fish(ctx, x - w * .18, y - 64, 1.1, -1.9, '#FF8A3D'); fish(ctx, x + w * .16, y - 66, 1, -1.25, '#FFC93C'); }
    g.shadowOn(ctx, 14, 7);
    ctx.beginPath(); ctx.moveTo(x - w / 2 - 6, y - 58); ctx.lineTo(x + w / 2 + 6, y - 58); ctx.lineTo(x + w / 2 - 10, y + 7); ctx.lineTo(x - w / 2 + 10, y + 7); ctx.closePath();
    ctx.fillStyle = g.lg(ctx, y - 58, y + 7, '#9FD6FF', '#4A8FD0'); ctx.fill(); g.shadowOff(ctx);
    ctx.lineWidth = 3; ctx.strokeStyle = INK; ctx.stroke();
    ctx.strokeStyle = 'rgba(40,70,130,.45)'; ctx.lineWidth = 5;
    [-40, -12].forEach(dy => { const k = (dy + 58) / 65; ctx.beginPath(); ctx.moveTo(x - w / 2 - 6 + 16 * k + 3, y + dy); ctx.lineTo(x + w / 2 + 6 - 16 * k - 3, y + dy); ctx.stroke(); });
    ctx.fillStyle = 'rgba(255,255,255,.5)'; g.rr(ctx, x - w / 2 + 6, y - 50, 10, 44, 5); ctx.fill();
    ctx.beginPath(); ctx.ellipse(x, y - 58, w / 2 + 6, 8, 0, 0, 7); ctx.fillStyle = '#2F5E93'; ctx.fill();
    // fish painted on the bucket
    fish(ctx, x, y - 26, 0.9, 0, full ? '#FFC93C' : '#FFE9A8');
    ctx.restore();
  },
  front(ctx: Ctx2D, gl: GoalArea) {
    const { x, y, w } = gl;
    ctx.save(); ctx.beginPath(); ctx.ellipse(x, y - 58, w / 2 + 6, 8, 0, 0, Math.PI); ctx.lineWidth = 5; ctx.strokeStyle = '#CFEAFF'; ctx.stroke();
    ctx.lineWidth = 2; ctx.strokeStyle = INK; ctx.beginPath(); ctx.ellipse(x, y - 58, w / 2 + 8, 10, 0, 0.1, Math.PI - 0.1); ctx.stroke(); ctx.restore();
  },
};

// Familiar things: the plank is a ski (slippery!), the pad is a seal, the stone a big snowball
const ski: Reskin = (ctx, pl, sel, g, rects) => {
  const r = rects[0];
  ctx.save(); ctx.translate(r.x, r.y); ctx.rotate(r.a);
  const w = r.w, h = r.h;
  g.shadowOn(ctx, 10, 5);
  ctx.beginPath(); ctx.moveTo(-w / 2 + 8, -h / 2); ctx.lineTo(w / 2 - 14, -h / 2); ctx.quadraticCurveTo(w / 2 + 6, -h / 2 - 2, w / 2 + 8, -h / 2 - 16);
  ctx.quadraticCurveTo(w / 2 + 12, h / 2, w / 2 - 14, h / 2); ctx.lineTo(-w / 2 + 8, h / 2); ctx.quadraticCurveTo(-w / 2, h / 2, -w / 2, 0); ctx.quadraticCurveTo(-w / 2, -h / 2, -w / 2 + 8, -h / 2); ctx.closePath();
  ctx.fillStyle = g.lg(ctx, -h / 2, h / 2, '#FF8A8A', '#D8344A'); ctx.fill(); g.shadowOff(ctx);
  ctx.lineWidth = 3; ctx.strokeStyle = INK; ctx.stroke();
  ctx.fillStyle = '#fff'; g.rr(ctx, -w / 2 + 14, -3, w - 40, 6, 3); ctx.fill();
  ctx.fillStyle = '#3B3F55'; g.rr(ctx, -16, -h / 2 - 5, 32, 9, 4); ctx.fill();
  if (sel) { g.rr(ctx, -w / 2 - 5, -h / 2 - 20, w + 18, h + 26, 10); ctx.strokeStyle = '#FFC93C'; ctx.lineWidth = 4; ctx.stroke(); }
  ctx.restore();
};
const seal: Reskin = (ctx, pl, sel, g, rects) => {
  const r = rects[0];
  ctx.save(); ctx.translate(r.x, r.y); ctx.rotate(r.a);
  g.shadowOn(ctx, 10, 5);
  ctx.beginPath(); ctx.ellipse(0, 6, 58, 16, 0, 0, 7); ctx.fillStyle = g.lg(ctx, -10, 22, '#B9C4D8', '#7C89A3'); ctx.fill(); g.shadowOff(ctx);
  ctx.lineWidth = 3; ctx.strokeStyle = INK; ctx.stroke();
  ctx.beginPath(); ctx.ellipse(4, -2, 40, 8, 0, 0, 7); ctx.fillStyle = '#E8EDF5'; ctx.fill();            // belly up
  ctx.fillStyle = '#7C89A3'; [-1, 1].forEach(s => { ctx.beginPath(); ctx.ellipse(56, 6 + s * 8, 12, 5, s * .5, 0, 7); ctx.fill(); ctx.stroke(); });
  ctx.beginPath(); ctx.arc(-52, 2, 16, 0, 7); ctx.fillStyle = '#AEB9CE'; ctx.fill(); ctx.stroke();
  ctx.fillStyle = INK; ctx.beginPath(); ctx.arc(-58, -3, 2.6, 0, 7); ctx.arc(-48, -5, 2.6, 0, 7); ctx.fill();
  ctx.beginPath(); ctx.ellipse(-60, 6, 4, 3, 0, 0, 7); ctx.fill();
  ctx.strokeStyle = 'rgba(59,42,92,.6)'; ctx.lineWidth = 1.5; ctx.beginPath(); ctx.moveTo(-64, 8); ctx.lineTo(-76, 6); ctx.moveTo(-64, 10); ctx.lineTo(-75, 13); ctx.stroke();
  if (sel) { g.rr(ctx, -74, -14, 148, 40, 14); ctx.strokeStyle = '#FFC93C'; ctx.lineWidth = 4; ctx.stroke(); }
  ctx.restore();
};
const snowball: Reskin = (ctx, pl, sel, g) => {
  ctx.save(); ctx.translate(pl.x, pl.y); ctx.rotate((pl.a || 0) * DEG);
  g.shadowOn(ctx, 10, 6);
  const gr = ctx.createRadialGradient(-8, -10, 4, 0, 0, 28); gr.addColorStop(0, '#FFFFFF'); gr.addColorStop(1, '#A9C6E2');
  ctx.beginPath(); ctx.arc(0, 0, 24, 0, 7); ctx.fillStyle = gr; ctx.fill(); g.shadowOff(ctx);
  ctx.lineWidth = 3; ctx.strokeStyle = INK; ctx.stroke();
  ctx.fillStyle = 'rgba(120,160,210,.5)'; [[7, 8, 4], [-9, 6, 3], [8, -9, 2.5]].forEach(([px, py, rr]) => { ctx.beginPath(); ctx.arc(px, py, rr, 0, 7); ctx.fill(); });
  if (sel) { ctx.beginPath(); ctx.arc(0, 0, 31, 0, 7); ctx.strokeStyle = '#FFC93C'; ctx.lineWidth = 4; ctx.stroke(); }
  ctx.restore();
};
const reskin = { plank: ski, pad: seal, stone: snowball };
const icons = {
  plank(ctx: Ctx2D, g: Gfx) { ski(ctx, {} as Placement, false, g, [{ x: -4, y: 6, w: 100, h: 16, a: -0.35 }]); },
  pad(ctx: Ctx2D, g: Gfx) { ctx.save(); ctx.scale(.75, .75); seal(ctx, {} as Placement, false, g, [{ x: 4, y: 0, w: 100, h: 16, a: 0 }]); ctx.restore(); },
  stone(ctx: Ctx2D, g: Gfx) { snowball(ctx, { x: 0, y: 0, a: 0 } as Placement, false, g, []); },
};

// ---------- meta ----------
const meta: WorldMeta = {
  icon: '🐧', strings, music: 'penguins.mp3',
  credits: { title: 'Ethereal Winter Ballet 02', artist: 'alanajordan', id: 475693, url: 'https://pixabay.com/music/orchestral-ethereal-winter-ballet-instrumental-02-475693/' },
  synth: { bpm: 60, root: 293.7, sc: 'major', prog: [0, 4, 5, 3], bar: 3, lead: 'bell', dens: 0.3 },
};

const world = defineWorld({
  k: K, meta, scene, wash: 'rgba(226,242,252,.55)', setup,
  skins, parts, objs, sounds, reskin, icons, ball: drawChick, goal,
  levels,
});
export default world;
